import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Home, BookOpen, DollarSign, Camera, Users, FileText, MessageSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Resources = ({ user }) => {
  const navigate = useNavigate();
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [guide, setGuide] = useState("");
  const [loading, setLoading] = useState(false);

  const topics = [
    {
      id: "pricing",
      title: "Pricing Your Home",
      icon: DollarSign,
      color: "orange",
      description: "Learn how to price your FSBO home competitively in the DFW market"
    },
    {
      id: "photos",
      title: "Professional Photos",
      icon: Camera,
      color: "blue",
      description: "Tips for taking stunning listing photos that sell"
    },
    {
      id: "showings",
      title: "Conducting Showings",
      icon: Users,
      color: "green",
      description: "Safe and effective strategies for showing your home"
    },
    {
      id: "negotiations",
      title: "Negotiation Tactics",
      icon: MessageSquare,
      color: "purple",
      description: "Master the art of negotiating offers and counteroffers"
    },
    {
      id: "paperwork",
      title: "Texas Paperwork",
      icon: FileText,
      color: "red",
      description: "Essential documents and disclosures for Texas FSBO sales"
    }
  ];

  const loadGuide = async (topicId) => {
    setSelectedTopic(topicId);
    setLoading(true);
    setGuide("");
    
    try {
      const response = await axios.get(`${API}/resources/guide/${topicId}`);
      setGuide(response.data.content);
    } catch (error) {
      toast.error("Failed to load guide");
      setSelectedTopic(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      <Helmet>
        <title>FSBO Seller Resources & Tools | Its-Sold.com</title>
        <meta name="description" content="Free resources and tools for selling your home FSBO in DFW. AI listing descriptions, pricing tools, marketing tips, and more." />
        <link rel="canonical" href="https://its-sold.com/resources" />
      </Helmet>
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>Its-Sold.com<sup className="text-xs" style={{ fontSize: "0.6em", verticalAlign: "super" }}>™</sup></span>
            </div>
            <Button onClick={() => navigate('/')} variant="outline" data-testid="back-home-btn">
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm mb-4">
            <Sparkles className="w-4 h-4" />
            <span>AI-Powered Educational Guides</span>
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display' }}>FSBO Success Center</h1>
          <p className="text-xl text-gray-600">Expert guides to help you sell your Dallas/Fort Worth home successfully</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {topics.map(topic => {
            const Icon = topic.icon;
            const colorClasses = {
              orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200' },
              blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200' },
              green: { bg: 'bg-green-100', text: 'text-green-600', border: 'border-green-200' },
              purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200' },
              red: { bg: 'bg-red-100', text: 'text-red-600', border: 'border-red-200' }
            };
            const colors = colorClasses[topic.color];

            return (
              <Card
                key={topic.id}
                onClick={() => loadGuide(topic.id)}
                className={`p-6 cursor-pointer hover-lift border-2 ${colors.border} ${selectedTopic === topic.id ? 'ring-2 ring-offset-2 ring-' + topic.color + '-600' : ''}`}
                data-testid={`topic-card-${topic.id}`}
              >
                <div className={`w-16 h-16 ${colors.bg} rounded-2xl flex items-center justify-center mb-4`}>
                  <Icon className={`w-8 h-8 ${colors.text}`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>{topic.title}</h3>
                <p className="text-sm text-gray-600">{topic.description}</p>
              </Card>
            );
          })}
        </div>

        {selectedTopic && (
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-orange-100" data-testid="guide-content">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <BookOpen className="w-6 h-6 text-orange-600" />
                <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>
                  {topics.find(t => t.id === selectedTopic)?.title}
                </h2>
              </div>
              <div className="flex items-center space-x-2 bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs">
                <Sparkles className="w-3 h-3" />
                <span>Generated by Claude AI</span>
              </div>
            </div>
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-orange-600 mb-4"></div>
                <p className="text-gray-600">Generating your personalized guide...</p>
              </div>
            ) : (
              <div className="prose prose-lg max-w-none">
                <div className="text-gray-700 leading-relaxed whitespace-pre-wrap">{guide}</div>
              </div>
            )}
          </div>
        )}

        {!selectedTopic && (
          <div className="text-center py-12 bg-white rounded-2xl border border-orange-100">
            <BookOpen className="w-16 h-16 mx-auto mb-4 text-orange-600" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display' }}>Select a Topic to Begin</h3>
            <p className="text-gray-600">Click any guide above to access AI-powered educational content</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Resources;